import React, { useState } from "react";
import { motion } from "framer-motion";
import { fadeUp, softReveal, staggerParent, viewportOnce } from "./motion";

const bullets = [
  "Initial response within 24 business hours",
  "Clear next steps based on your goals",
  "Recommendations based on your business needs",
];

const trustSignals = [
  { value: "24 hrs", label: "Typical first response" },
  { value: "Goal-led", label: "Recommendations only" },
  { value: "Clear next step", label: "After every conversation" },
];

const contactChannels = [
  {
    title: "Office Address",
    body: "WorkFlo Hitex Bizness Square, Madhapur, Hyderabad - 500081",
    href: "https://www.google.com/maps/place/dYzital/@17.45653,78.3737651,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcb93cd779f498b:0x737600281afc24b4!8m2!3d17.45653!4d78.37634!16s%2Fg%2F11z15mw8xh?entry=ttu&g_ep=EgoyMDI2MDMxMS4wIKXMDSoASAFQAw%3D%3D",
    linkLabel: "Open in Maps",
  },
  {
    title: "General Enquiries",
    body: "For partnerships, service discussions, and project enquiries.",
    href: "mailto:info@dyzital.com",
    linkLabel: "info@dyzital.com",
  },
  {
    title: "Careers & Opportunities",
    body: "For team, freelance, and collaboration conversations.",
    href: "mailto:hr@dyzital.com",
    linkLabel: "hr@dyzital.com",
  },
];

const Contact = () => {
  const [frameHeight, setFrameHeight] = useState(620);

  const handleFrameLoad = (event) => {
    try {
      const doc = event.target.contentWindow?.document;
      const formWrap = doc?.getElementById("crmWebToEntityForm");
      const formEl = doc?.getElementById("webform979740000000674003");
      const nextHeight = Math.max(
        formWrap?.scrollHeight || 0,
        formEl?.scrollHeight || 0,
        doc?.body?.scrollHeight || 0
      );
      if (nextHeight && Number.isFinite(nextHeight)) {
        setFrameHeight(nextHeight + 6);
      }
    } catch {
      // Keep fallback height if the browser blocks frame document access.
    }
  };

  return (
    <motion.section
      id="contact"
      className="contact-section"
      variants={staggerParent(0.06, 0.12)}
      initial="hidden"
      whileInView="show"
      viewport={viewportOnce}
    >
      <div className="contact-grid">
        <motion.div className="contact-form" variants={softReveal}>
          <motion.div className="contact-intro" variants={staggerParent(0.08, 0.08)}>
            <motion.p className="section-kicker" variants={fadeUp}>
              Contact Us
            </motion.p>
            <motion.h1 variants={fadeUp}>Let’s Talk About Growing Your Business Online</motion.h1>
            <motion.p className="contact-subtitle desktop-copy" variants={fadeUp}>
              Tell us about your business, what support you need, and what result you want from your digital marketing.
            </motion.p>
            <motion.p className="contact-subtitle mobile-copy" variants={fadeUp}>
              Share your business goal and we’ll suggest the next practical step.
            </motion.p>

            <motion.div className="contact-points" variants={staggerParent(0.1, 0.08)}>
              {bullets.map((item) => (
                <motion.p key={item} variants={fadeUp}>
                  {item}
                </motion.p>
              ))}
            </motion.div>

            <motion.div className="contact-trust-strip" variants={staggerParent(0.12, 0.08)}>
              {trustSignals.map((signal) => (
                <motion.div key={signal.label} className="contact-trust-card" variants={softReveal}>
                  <strong>{signal.value}</strong>
                  <span>{signal.label}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div className="form-shell" variants={softReveal}>
            <iframe
              className="zoho-frame"
              src="/zoho-lead-form.html"
              width="100%"
              height={frameHeight}
              scrolling="no"
              style={{ border: "none" }}
              title="Zoho Lead Capture Form"
              onLoad={handleFrameLoad}
            />
          </motion.div>
        </motion.div>

        <motion.aside className="contact-info" variants={softReveal}>
          <motion.p className="section-kicker" variants={fadeUp}>
            Reach Out Anytime
          </motion.p>
          <motion.h3 variants={fadeUp}>Direct contact, clear communication, and practical support.</motion.h3>
          <motion.p className="contact-info-copy desktop-copy" variants={fadeUp}>
            If you would like to speak with us before filling out the full form, you can contact us through any of these channels.
          </motion.p>
          <motion.p className="contact-info-copy mobile-copy" variants={fadeUp}>
            Prefer direct contact? Use any channel below.
          </motion.p>

          <motion.div className="contact-info-list" variants={staggerParent(0.12, 0.08)}>
            {contactChannels.map((item) => (
              <motion.div key={item.title} className="info-item" variants={fadeUp}>
                <strong>{item.title}</strong>
                <p>{item.body}</p>
                <a href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel={item.href.startsWith("http") ? "noreferrer" : undefined}>
                  {item.linkLabel}
                </a>
              </motion.div>
            ))}
          </motion.div>

          <motion.div className="contact-note-card" variants={softReveal}>
            <p className="contact-note-kicker">Best fit</p>
            <p>
              Best for businesses that want practical digital marketing support with clear communication and measurable goals.
            </p>
          </motion.div>
        </motion.aside>
      </div>
    </motion.section>
  );
};

export default Contact;
